import React, { useState, useEffect, useRef } from "react";
import { 
  Search, MapPin, MessageSquare, Heart, X, Loader2, 
  Image as ImageIcon, ArrowLeft, InboxIcon, Send, ShieldCheck,
  ChevronLeft, ChevronRight, LayoutGrid, Images
} from "lucide-react";
import { useNavigate, Link } from "react-router-dom"; 
import Navbar from "../Navbar/Navbar";
import Footer from "../Footer/Footer"; 
import Disclaimer from "../Legal/Disclaimer";

const SHOP_CATEGORIES = [
  { id: "All", label: "All" },
  // Escort-vehicle equipment essentials first
  { id: "oversize_signs", label: "Signs" },
  { id: "lights_strobes", label: "Lights" },
  { id: "roof_racks_mounts", label: "Mounts" },
  { id: "cb_radios_comm", label: "Electronics" },
  { id: "flags_safety", label: "Safety Gear" },
  { id: "height_poles", label: "High-Poles" },
  // Vehicles, machinery, and bundles after core escort gear
  { id: "vehicles", label: "Cars & Trucks" },
  { id: "heavy_equipment", label: "Heavy Equipment" },
  { id: "complete_kits", label: "Kits" },
  { id: "bundles", label: "Bundles" },
  { id: "personal_items", label: "Personal Items" },
  { id: "other", label: "Other" },
];

const QUICK_QUESTIONS = [
  "Is this still available?",
  "Any damage or issues?",
  "Can you send more photos?",
  "What's your best price?",
];

// ─── NAME FORMATTER ───────────────────────────────────────────────────────────
const formatName = (userObjOrString) => {
  if (!userObjOrString) return "Seller";
  let rawString = "";
  if (typeof userObjOrString === 'object') {
    if (userObjOrString.first_name || userObjOrString.last_name) {
      rawString = `${userObjOrString.first_name || ''} ${userObjOrString.last_name || ''}`.trim();
    } else {
      rawString = userObjOrString.name || userObjOrString.username || userObjOrString.email || "";
    }
  } else {
    rawString = String(userObjOrString);
  }
  if (!rawString || rawString === "undefined" || rawString === "null") return "Seller";
  const namePart = rawString.split('@')[0];
  const parts = namePart.split(/[\s._-]+/).filter(Boolean);
  if (parts.length > 1) {
    const first = parts[0].charAt(0).toUpperCase() + parts[0].slice(1).toLowerCase();
    const lastInitial = parts[parts.length - 1].charAt(0).toUpperCase();
    return `${first} ${lastInitial}.`;
  } else if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase() + parts[0].slice(1).toLowerCase();
  }
  return "Seller";
};

// Look up a friendly label for a listing's category id (falls back to the raw id)
const categoryLabel = (id) => SHOP_CATEGORIES.find(c => c.id === id)?.label || id;

// Single source of truth for the verified badge — matches the actual backend
// field (TradeLinkListing.is_ppca_verified). Never self-declared by the UI.
const isVerifiedListing = (item) => Boolean(item?.is_ppca_verified);

const getInitials = (formattedName) => {
  if (!formattedName || formattedName === "Seller") return "SE";
  const parts = formattedName.split(" ");
  if (parts.length > 1) return (parts[0][0] + parts[1][0]).toUpperCase();
  return formattedName.substring(0, 2).toUpperCase();
};

// ─── RELATIVE TIME HELPER ─────────────────────────────────────────────────────
const timeAgo = (dateStr) => {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString([], { month: "short", day: "numeric" });
};

// ─── STATUS TICKS ─────────────────────────────────────────────────────────────
function StatusTick({ isRead, size = 14 }) {
  const color = isRead ? "#3BA3FF" : "#A0A0A0";
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 0, flexShrink: 0 }}>
      <svg width={size} height={size} viewBox="0 0 13 13" fill="none">
        <polyline points="2,7 5,10 11,3" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      {isRead && (
        <svg width={size} height={size} viewBox="0 0 13 13" fill="none" style={{ marginLeft: -7 }}>
          <polyline points="2,7 5,10 11,3" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </span>
  );
}

// ─── IMAGE GALLERY (multi-photo browsing for the detail views) ───────────────
// Give this a `key={item.id}` wherever it's used so the index resets when the
// selected listing changes, instead of carrying over the previous item's index.
function ImageGallery({ images, alt, height, fallbackEmoji = "📦" }) {
  const [idx, setIdx] = useState(0);
  const touchStartX = useRef(null);
  const hasImages = images && images.length > 0;
  const count = hasImages ? images.length : 0;

  const go = (dir) => {
    if (count < 2) return;
    setIdx(prev => (prev + dir + count) % count);
  };

  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 40) go(delta > 0 ? -1 : 1);
    touchStartX.current = null;
  };

  if (!hasImages) {
    return (
      <div style={{ width:"100%", height, display:"flex", alignItems:"center", justifyContent:"center", fontSize:48 }}>
        {fallbackEmoji}
      </div>
    );
  }

  return (
    <div
      style={{ position:"relative", width:"100%", height, overflow:"hidden", touchAction:"pan-y" }}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <img src={images[idx].image} alt={alt} style={{ width:"100%", height:"100%", objectFit:"cover" }}/>

      {count > 1 && (
        <>
          <button onClick={e=>{e.stopPropagation(); go(-1);}} aria-label="Previous photo"
            style={{ position:"absolute", left:8, top:"50%", transform:"translateY(-50%)", width:32, height:32, borderRadius:"50%", background:"rgba(0,0,0,0.45)", border:"none", color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", backdropFilter:"blur(4px)" }}>
            <ChevronLeft size={18}/>
          </button>
          <button onClick={e=>{e.stopPropagation(); go(1);}} aria-label="Next photo"
            style={{ position:"absolute", right:8, top:"50%", transform:"translateY(-50%)", width:32, height:32, borderRadius:"50%", background:"rgba(0,0,0,0.45)", border:"none", color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", backdropFilter:"blur(4px)" }}>
            <ChevronRight size={18}/>
          </button>

          {/* Dot indicators */}
          <div style={{ position:"absolute", bottom:8, left:"50%", transform:"translateX(-50%)", display:"flex", gap:5, zIndex:2 }}>
            {images.map((_,i)=>(
              <div key={i} onClick={e=>{e.stopPropagation(); setIdx(i);}}
                style={{ width:i===idx?16:6, height:6, borderRadius:99, background:i===idx?"#fff":"rgba(255,255,255,0.5)", cursor:"pointer", transition:"width .15s" }}/>
            ))}
          </div>

          {/* Photo counter — top-left, since top-right/bottom-left are used by close/condition badges */}
          <div style={{ position:"absolute", top:8, left:8, padding:"2px 8px", borderRadius:99, background:"rgba(0,0,0,0.55)", color:"#fff", fontSize:10, fontWeight:700, backdropFilter:"blur(4px)" }}>
            {idx+1}/{count}
          </div>
        </>
      )}
    </div>
  );
}

// ─── INLINE DESKTOP SIDEBAR CHAT ──────────────────────────────────────────────
function InlineSidebarChat({ currentItem, onClose }) {
  const [activeConvoId, setActiveConvoId] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(true);
  const bottomRef = useRef(null);
  const wsRef = useRef(null);
  const authToken = localStorage.getItem('token');
  const apiUrl = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
  const userStr = localStorage.getItem('user');
  let loggedInUser = {};
  try { if (userStr) loggedInUser = JSON.parse(userStr); } catch (e) {}
  const myId = loggedInUser?.id || loggedInUser?.pk;
  const myUsername = loggedInUser?.username || loggedInUser?.name || loggedInUser?.email || localStorage.getItem('username');

  useEffect(() => {
    const startConvo = async () => {
      try {
        const startRes = await fetch(`${apiUrl}/api/tradelink/chat/start/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
          body: JSON.stringify({ listing_id: currentItem.id })
        });
        if (startRes.ok) {
          const startData = await startRes.json();
          setActiveConvoId(startData.conversation_id);
        }
      } catch (err) {
        console.error("Failed to start chat:", err);
      } finally {
        setLoading(false);
      }
    };
    if (authToken && currentItem) startConvo();
  }, [currentItem, authToken, apiUrl]);

  useEffect(() => {
    if (!activeConvoId) return;
    const fetchHistory = async () => {
      try {
        const res = await fetch(`${apiUrl}/api/tradelink/chat/${activeConvoId}/history/`, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        });
        if (res.ok) {
          const data = await res.json();
          setMsgs(data.results || data);
        }
      } catch (err) {}
    };
    fetchHistory();
    let wsBaseUrl = apiUrl.startsWith("https://") 
      ? apiUrl.replace("https://", "wss://") 
      : apiUrl.replace("http://", "ws://");
    const socketUrl = `${wsBaseUrl}/ws/chat/tradelink/${activeConvoId}/?token=${authToken}`;
    const chatSocket = new WebSocket(socketUrl);
    wsRef.current = chatSocket;
    chatSocket.onmessage = (event) => {
      const newMsg = JSON.parse(event.data);
      setMsgs(prev => {
        const existingIndex = prev.findIndex(m => m.id === newMsg.id);
        if (existingIndex !== -1) {
          const updatedMsgs = [...prev];
          updatedMsgs[existingIndex] = { ...updatedMsgs[existingIndex], ...newMsg };
          return updatedMsgs;
        }
        const optimisticIndex = prev.findIndex(m => m.isOptimistic && m.content === newMsg.content);
        if (optimisticIndex !== -1) {
          const updatedMsgs = [...prev];
          updatedMsgs[optimisticIndex] = { ...newMsg }; 
          return updatedMsgs;
        }
        return [...prev, newMsg];
      });
    };
    return () => { if (chatSocket.readyState === WebSocket.OPEN) chatSocket.close(); };
  }, [activeConvoId, authToken, apiUrl]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [msgs]);

  const handleSend = async (textToSend) => {
    if (!textToSend.trim() || !activeConvoId) return;
    setInput("");
    const tempMsg = {
      id: `temp-${Date.now()}`, sender: myId, sender_name: myUsername, content: textToSend,
      is_read: false, isOptimistic: true, created_at: new Date().toISOString()
    };
    setMsgs(prev => [...prev, tempMsg]);
    try {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ message: textToSend }));
      } else {
        await fetch(`${apiUrl}/api/tradelink/chat/send/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
          body: JSON.stringify({ conversation_id: activeConvoId, text: textToSend })
        });
      }
    } catch (err) { console.error("Failed to send:", err); }
  };

  const sellerName = formatName(currentItem.seller);
  return (
    <div className="border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden bg-slate-50 dark:bg-slate-800/50 flex flex-col h-[400px]">
      <div className="flex items-center justify-between p-3 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 shrink-0">
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-full bg-cyan-100 text-cyan-700 flex items-center justify-center font-bold text-xs uppercase">
            {getInitials(sellerName)}
          </div>
          <span className="text-sm font-bold text-slate-900 dark:text-white truncate max-w-[150px]">{sellerName}</span>
        </div>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300">
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-3 flex flex-col gap-2">
        {loading ? (
          <div className="flex-1 flex justify-center items-center text-sm text-slate-400">Connecting...</div>
        ) : msgs.length === 0 ? (
          <div className="mt-auto space-y-3">
            <div className="px-1"><Disclaimer variant="marketplace" /></div>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest text-center mb-2">Quick Questions</p>
            <div className="flex flex-wrap gap-1.5 justify-center">
              {QUICK_QUESTIONS.map((q) => (
                <button key={q} onClick={() => handleSend(q)} className="bg-white border border-slate-200 text-slate-600 text-xs px-2.5 py-1.5 rounded-full hover:bg-slate-50 hover:border-cyan-300 transition-colors">{q}</button>
              ))}
            </div>
          </div>
        ) : (
          msgs.map((m, index) => {
            const isMe = m.isOptimistic || Boolean(
              (myId && (String(m.sender) === String(myId) || String(m.sender_id) === String(myId))) || 
              (myUsername && m.sender_name && (String(m.sender_name).toLowerCase() === String(myUsername).toLowerCase()))
            );
            return (
              <div key={m.id || `msg-${index}`} className={`flex w-full ${isMe ? 'justify-end' : 'justify-start'}`}>
                <div className={`flex flex-col max-w-[85%] ${isMe ? 'items-end' : 'items-start'}`}>
                  <div className={`px-3 py-1.5 text-sm shadow-sm ${isMe ? 'bg-cyan-600 text-white rounded-[12px_12px_4px_12px]' : 'bg-white border border-slate-200 text-slate-900 rounded-[12px_12px_12px_4px]'}`}>{m.content}</div>
                  <div className="flex items-center gap-1 mt-0.5 px-1">
                    <span className="text-[9px] text-slate-400 font-medium">{new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                    {isMe && <StatusTick isRead={m.is_read} size={10} />}
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={bottomRef} />
      </div>
      <div className="p-2 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700 flex gap-2 shrink-0">
        <input value={input} onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && input.trim()) handleSend(input); }}
          placeholder="Type a message..."
          className="flex-1 bg-slate-100 dark:bg-slate-800 border-none rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-white focus:ring-2 focus:ring-cyan-500 outline-none"
        />
        <button onClick={() => handleSend(input)} disabled={!input.trim()}
          className="bg-[#0891b2] hover:bg-[#06b6d4] disabled:opacity-50 text-white px-3 py-2 rounded-lg font-bold text-sm transition-colors flex items-center justify-center">
          <Send className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// ─── PREMIUM MESSAGING MODAL ──────────────────────────────────────────────────
function PremiumMessagingCenter({ currentItem, onClose }) {
  const [activeConvoId, setActiveConvoId] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(true);
  const bottomRef = useRef(null);
  const wsRef = useRef(null);
  const authToken = localStorage.getItem('token');
  const apiUrl = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
  const userStr = localStorage.getItem('user');
  let loggedInUser = {};
  try { if (userStr) loggedInUser = JSON.parse(userStr); } catch (e) {}
  const myId = loggedInUser?.id || loggedInUser?.pk;
  const myUsername = loggedInUser?.username || loggedInUser?.name || loggedInUser?.email || localStorage.getItem('username');

  useEffect(() => {
    const fetchInboxAndStart = async () => {
      try {
        const inboxRes = await fetch(`${apiUrl}/api/tradelink/chat/inbox/`, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        });
        let fetchedConvos = [];
        if (inboxRes.ok) {
          const data = await inboxRes.json();
          fetchedConvos = Array.isArray(data) ? data : (data.results || []);
          setConversations(fetchedConvos);
        }
        if (currentItem) {
          const existingConvo = fetchedConvos.find(c => c.listing_title === currentItem.title);
          if (existingConvo) {
            setActiveConvoId(existingConvo.id);
          } else {
            const startRes = await fetch(`${apiUrl}/api/tradelink/chat/start/`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
              body: JSON.stringify({ listing_id: currentItem.id })
            });
            const startData = await startRes.json();
            if (startRes.ok) {
              setActiveConvoId(startData.conversation_id);
              const newInboxRes = await fetch(`${apiUrl}/api/tradelink/chat/inbox/`, {
                headers: { 'Authorization': `Bearer ${authToken}` }
              });
              if (newInboxRes.ok) {
                const newData = await newInboxRes.json();
                setConversations(Array.isArray(newData) ? newData : (newData.results || []));
              }
            }
          }
        }
      } catch (err) { console.error(err); } finally { setLoading(false); }
    };
    if (authToken) fetchInboxAndStart();
  }, [currentItem, authToken, apiUrl]);

  useEffect(() => {
    if (!activeConvoId) return;
    const fetchHistory = async () => {
      try {
        const res = await fetch(`${apiUrl}/api/tradelink/chat/${activeConvoId}/history/`, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        });
        if (res.ok) {
          const data = await res.json();
          setMsgs(data.results || data);
        }
      } catch (err) {}
    };
    fetchHistory();
    let wsBaseUrl = apiUrl.startsWith("https://") ? apiUrl.replace("https://", "wss://") : apiUrl.replace("http://", "ws://");
    const socketUrl = `${wsBaseUrl}/ws/chat/tradelink/${activeConvoId}/?token=${authToken}`;
    const chatSocket = new WebSocket(socketUrl);
    wsRef.current = chatSocket;
    chatSocket.onmessage = (event) => {
      const newMsg = JSON.parse(event.data);
      setMsgs(prev => {
        const existingIndex = prev.findIndex(m => m.id === newMsg.id);
        if (existingIndex !== -1) {
          const updatedMsgs = [...prev];
          updatedMsgs[existingIndex] = { ...updatedMsgs[existingIndex], ...newMsg };
          return updatedMsgs;
        }
        const optimisticIndex = prev.findIndex(m => m.isOptimistic && m.content === newMsg.content);
        if (optimisticIndex !== -1) {
          const updatedMsgs = [...prev];
          updatedMsgs[optimisticIndex] = { ...newMsg }; 
          return updatedMsgs;
        }
        return [...prev, newMsg];
      });
    };
    return () => { if (chatSocket.readyState === WebSocket.OPEN) chatSocket.close(); };
  }, [activeConvoId, authToken, apiUrl]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [msgs, activeConvoId]);

  const sendMessage = async () => {
    if (!input.trim() || !activeConvoId) return;
    const text = input.trim();
    setInput("");
    const tempMsg = {
      id: `temp-${Date.now()}`, sender: myId, sender_name: myUsername, content: text,
      is_read: false, isOptimistic: true, created_at: new Date().toISOString()
    };
    setMsgs(prev => [...prev, tempMsg]);
    try {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ message: text }));
      } else {
        await fetch(`${apiUrl}/api/tradelink/chat/send/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
          body: JSON.stringify({ conversation_id: activeConvoId, text })
        });
      }
    } catch (err) {}
  };

  const activeConvoData = conversations.find(c => c.id === activeConvoId);
  const partnerName = activeConvoData 
    ? formatName({ first_name: activeConvoData.partner_first_name, last_name: activeConvoData.partner_last_name, name: activeConvoData.partner_name, email: activeConvoData.partner_email })
    : formatName(currentItem?.seller);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl w-full max-w-5xl h-[85vh] shadow-2xl flex overflow-hidden border border-slate-200" onClick={e => e.stopPropagation()}>
        <div className={`w-full md:w-1/3 bg-slate-50 border-r border-slate-200 flex flex-col ${activeConvoId ? 'hidden md:flex' : 'flex'}`}>
          <div className="p-5 border-b border-slate-200 bg-white flex justify-between items-center">
            <h2 className="text-xl font-extrabold text-slate-900 tracking-tight">Messages</h2>
            <button onClick={onClose} className="md:hidden text-slate-400 hover:text-slate-600"><X className="w-6 h-6" /></button>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading ? ( <div className="p-8 text-center text-slate-400 font-medium animate-pulse">Loading inbox...</div> ) : conversations.map(convo => {
              const rowName = formatName({ first_name: convo.partner_first_name, last_name: convo.partner_last_name, name: convo.partner_name, email: convo.partner_email });
              return (
                <div key={convo.id} onClick={() => setActiveConvoId(convo.id)} className={`p-4 border-b border-slate-200 cursor-pointer transition-all ${activeConvoId === convo.id ? 'bg-blue-50 border-l-4 border-l-blue-500' : 'bg-white hover:bg-slate-100 border-l-4 border-l-transparent'}`}>
                  <div className="flex justify-between items-start mb-1">
                    <div className="font-bold text-slate-900 text-sm truncate pr-2">{convo.listing_title}</div>
                    {convo.unread_count > 0 && (
                      <div className="bg-blue-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">{convo.unread_count}</div>
                    )}
                  </div>
                  <div className="text-xs font-medium text-slate-500 mt-1">{rowName}</div>
                </div>
              );
            })}
          </div>
        </div>
        {activeConvoId && (
          <div className={`flex-1 flex flex-col bg-white relative ${!activeConvoId ? 'hidden md:flex' : 'flex'}`}>
            <div className="px-4 md:px-6 py-4 border-b border-slate-200 flex justify-between items-center bg-white z-10">
              <div className="flex items-center gap-3">
                <button onClick={() => setActiveConvoId(null)} className="md:hidden w-8 h-8 flex items-center justify-center text-slate-500 hover:bg-slate-100 rounded-full"><ArrowLeft className="w-5 h-5" /></button>
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-cyan-100 text-cyan-700 flex items-center justify-center font-extrabold text-base md:text-lg border-2 border-cyan-200">{getInitials(partnerName)}</div>
                <div className="font-bold text-slate-900 text-base md:text-lg">{partnerName}</div>
              </div>
              <button onClick={onClose} className="w-8 h-8 flex items-center justify-center bg-slate-50 hover:bg-slate-100 text-slate-400 rounded-full border border-slate-200"><X className="w-4 h-4" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 md:p-6 bg-[#f0f2f5] flex flex-col gap-3">
              {msgs.map((m, index) => {
                const isMe = m.isOptimistic || Boolean(
                  (myId && (String(m.sender) === String(myId) || String(m.sender_id) === String(myId))) || 
                  (myUsername && m.sender_name && (String(m.sender_name).toLowerCase() === String(myUsername).toLowerCase()))
                );
                return (
                  <div key={m.id || `msg-${index}`} className={`flex w-full ${isMe ? 'justify-end' : 'justify-start'}`}>
                    <div className={`flex flex-col max-w-[85%] md:max-w-[75%] ${isMe ? 'items-end' : 'items-start'}`}>
                      <div className={`px-4 py-2 text-sm md:text-[15px] shadow-sm ${isMe ? 'bg-cyan-600 text-white rounded-[16px_16px_4px_16px]' : 'bg-white border border-slate-200 text-slate-900 rounded-[16px_16px_16px_4px]'}`}>{m.content}</div>
                      <div className="flex items-center gap-1.5 mt-1 px-1">
                        <span className="text-[10px] text-slate-400 font-medium">{new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                        {isMe && <StatusTick isRead={m.is_read} />}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={bottomRef} />
            </div>
            <div className="p-3 bg-[#f0f2f5] border-t border-slate-200">
              <div className="flex items-end gap-2 max-w-4xl mx-auto bg-white p-2 rounded-[24px] shadow-sm border border-slate-200">
                <textarea value={input} onChange={e => setInput(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                  placeholder="Message..." className="flex-1 bg-transparent px-4 py-2 outline-none text-slate-800 text-[15px] resize-none" style={{ minHeight: '40px', maxHeight: '100px' }}
                />
                <button onClick={sendMessage} disabled={!input.trim()} className="bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 text-white rounded-full h-10 w-10 flex items-center justify-center shrink-0"><Send className="w-4 h-4 ml-0.5" /></button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
export default function MarketplacePage({ isDarkMode, toggleTheme }) {
  const navigate = useNavigate();                          // ← replaces alert()
  const [activeCategory, setActiveCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [shopListings, setShopListings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showDesktopChat, setShowDesktopChat] = useState(false);
  const [showMobileModal, setShowMobileModal] = useState(false);
  const [showPremiumChat, setShowPremiumChat] = useState(false);
  const [savedItems, setSavedItems] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [viewMode, setViewMode] = useState("gallery"); // 'gallery' = larger photo-forward cards, 'grid' = dense compact cards

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
      if (window.innerWidth >= 1024) setShowMobileModal(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchShopListings = async () => {
      try {
        const apiUrl = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
        const response = await fetch(`${apiUrl}/api/tradelink/listings/`);
        if (response.ok) {
          const data = await response.json();
          const list = Array.isArray(data) ? data : (Array.isArray(data?.results) ? data.results : []);
          setShopListings(list);
        } else { setShopListings([]); }
      } catch (error) {
        console.error("Error fetching TradeLink data:", error);
        setShopListings([]);
      } finally { setIsLoading(false); }
    };
    fetchShopListings();
  }, []);

  const displayedItems = (Array.isArray(shopListings) ? shopListings : []).filter((item) => {
    const matchesCategory = activeCategory === "All" || item.category === activeCategory;
    const title = item?.title || "";
    const matchesSearch = title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          (item.description && item.description.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  const toggleSave = (e, id) => {
    e.stopPropagation();
    setSavedItems(prev => prev.includes(id) ? prev.filter(fId => fId !== id) : [...prev, id]);
  };

  const handleItemSelect = (item) => {
    setSelectedItem(item);
    if (isMobile) { setShowMobileModal(true); }
    else { setShowDesktopChat(false); }
  };

  const handleCloseSidebar = () => { setSelectedItem(null); setShowDesktopChat(false); };

  const handleMsgClick = (e, item) => {
    e.stopPropagation();
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');          // ← no more alert()
      return;
    }
    setSelectedItem(item);
    if (isMobile) {
      setShowMobileModal(false);
      setShowPremiumChat(true);
    } else {
      setShowDesktopChat(true);
    }
  };

  const handlePostListingClick = (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login', { state: { redirectTo: '/tradelink/post-listing' } });
      return;
    }
    navigate('/tradelink/post-listing');
  };

  // Design tokens
  const D       = isDarkMode;
  const bg      = D ? "#0d1f3c"                : "#f8fafc";
  const panelBg = D ? "#142a4d"                : "#ffffff";
  const midBg   = D ? "#1a3358"                : "#f1f5f9";
  const border  = D ? "rgba(255,255,255,0.10)" : "#e2e8f0";
  const text     = D ? "#f0f4f8"               : "#0f172a";
  const textMid  = D ? "rgba(240,244,248,0.75)": "#475569";
  const textSoft = D ? "rgba(240,244,248,0.50)": "#94a3b8";
  const inputBg  = D ? "rgba(255,255,255,0.06)": "#ffffff";
  const amber    = "#f59e0b";
  const amberL   = "#fbbf24";

  return (
    <div style={{ minHeight:"100vh", background:bg, fontFamily:"'Inter',sans-serif", transition:"background .3s" }}>
      <Navbar isDarkMode={isDarkMode} toggleTheme={toggleTheme} />

      {/* HERO BANNER */}
      <div style={{ background:`linear-gradient(135deg,#0d1f3c 0%,#1a3358 60%,#0f2d56 100%)`, padding:"clamp(24px,4vw,40px) clamp(16px,4vw,40px)", borderBottom:"3px solid #f59e0b", position:"relative", overflow:"hidden" }}>
        <div aria-hidden style={{ position:"absolute", inset:0, backgroundImage:"radial-gradient(rgba(255,255,255,0.05) 1px,transparent 1px)", backgroundSize:"20px 20px", pointerEvents:"none" }}/>
        <div aria-hidden style={{ position:"absolute", top:"-20%", right:"-5%", width:280, height:280, borderRadius:"50%", background:"radial-gradient(circle,rgba(251,191,36,0.12) 0%,transparent 70%)", pointerEvents:"none" }}/>
        <div style={{ maxWidth:1280, margin:"0 auto", display:"flex", flexWrap:"wrap", alignItems:"center", justifyContent:"space-between", gap:16, position:"relative", zIndex:1 }}>
          <div>
            <div style={{ fontSize:11, fontWeight:700, letterSpacing:".14em", color:amberL, textTransform:"uppercase", marginBottom:8 }}>Equipment Marketplace</div>
            <h1 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:"clamp(22px,4vw,34px)", fontWeight:900, color:"#fff", margin:"0 0 8px", lineHeight:1.15 }}>
              🛒 PilotLink <span style={{ color:amberL }}>TradeLink</span>
            </h1>
            <p style={{ fontSize:14, color:"rgba(255,255,255,0.65)", margin:0 }}>Pilot car equipment — bought and sold by verified professionals</p>
          </div>
          <div style={{ display:"flex", gap:20, flexWrap:"wrap" }}>
            {[["✅","Verified Sellers"],["🔒","Secure Messaging"],["📍","Location-Based"]].map(([ic,lb]) => (
              <div key={lb} style={{ display:"flex", alignItems:"center", gap:7, fontSize:12, color:"rgba(255,255,255,0.70)", fontWeight:500 }}>
                <span>{ic}</span>{lb}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={{ maxWidth:1280, margin:"0 auto", padding:"clamp(16px,3vw,28px) clamp(12px,3vw,24px)" }}>

        {/* SEARCH + POST */}
        <div style={{ display:"flex", gap:10, marginBottom:12, alignItems:"center" }}>
          <div style={{ position:"relative", flex:1, minWidth:0 }}>
            <Search size={15} style={{ position:"absolute", left:13, top:"50%", transform:"translateY(-50%)", color:textSoft, pointerEvents:"none" }}/>
            <input type="text" placeholder={isMobile ? "Search…" : "Search equipment, trucks, lights…"} value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              style={{ width:"100%", padding:"12px 36px 12px 38px", borderRadius:12, border:`1.5px solid ${border}`, background:inputBg, color:text, fontSize:15, outline:"none", fontFamily:"inherit", boxSizing:"border-box" }}
              onFocus={e=>e.target.style.borderColor=amber} onBlur={e=>e.target.style.borderColor=border}
            />
            {searchQuery && (
              <button onClick={()=>setSearchQuery("")} style={{ position:"absolute", right:10, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", color:textSoft }}>
                <X size={14}/>
              </button>
            )}
          </div>
          <button onClick={handlePostListingClick} style={{ display:"flex", alignItems:"center", gap:"6px", padding: isMobile ? "12px 16px" : "8px 16px", borderRadius:12, border:`1.5px solid ${amber}`, background:`linear-gradient(135deg,${amber},${amberL})`, color:"#0d1f3c", fontWeight:800, fontSize:isMobile?13:13, cursor:"pointer", textDecoration:"none", whiteSpace:"nowrap", fontFamily:"inherit", transition:"all .15s", boxShadow:`0 2px 10px rgba(245,158,11,0.35)`, flexShrink:0 }}>
            ➕ {isMobile ? "Post" : "Post Listing"}
          </button>
        </div>

        {/* CATEGORY PILLS — own full-width row, edge-to-edge scroll on mobile so it's easy to swipe/tap */}
        <div style={{ position:"relative", marginLeft: isMobile ? "clamp(-16px,-4vw,-12px)" : 0, marginRight: isMobile ? "clamp(-16px,-4vw,-12px)" : 0, marginBottom:20 }}>
          <div className="hide-scrollbar" style={{ display:"flex", gap:9, overflowX:"auto", flexWrap:"nowrap", scrollSnapType:"x proximity", WebkitOverflowScrolling:"touch", padding: isMobile ? "2px clamp(16px,4vw,12px) 6px" : "2px 0 6px" }}>
            {SHOP_CATEGORIES.map(c => {
              const active = activeCategory === c.id;
              return (
                <button key={c.id} onClick={()=>setActiveCategory(c.id)} style={{ padding: isMobile ? "11px 18px" : "8px 16px", minHeight: isMobile ? 42 : "auto", borderRadius:99, border:`1.5px solid ${active ? amber : border}`, background: active ? `linear-gradient(135deg,${amber},${amberL})` : panelBg, color: active ? "#0d1f3c" : textMid, fontWeight: active ? 800 : 600, fontSize:13, cursor:"pointer", whiteSpace:"nowrap", fontFamily:"inherit", transition:"all .15s", boxShadow: active ? `0 2px 10px rgba(245,158,11,0.35)` : "none", scrollSnapAlign:"start", flexShrink:0 }}>
                  {c.label}
                </button>
              );
            })}
          </div>
          {/* Fade edges hint that the row scrolls horizontally */}
          <div style={{ position:"absolute", top:0, bottom:6, left:0, width:isMobile?24:0, background:`linear-gradient(90deg, ${bg}, transparent)`, pointerEvents:"none" }}/>
          <div style={{ position:"absolute", top:0, bottom:6, right:0, width:isMobile?28:0, background:`linear-gradient(270deg, ${bg}, transparent)`, pointerEvents:"none" }}/>
        </div>

        {!isLoading && (
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:12, marginBottom:16 }}>
            <div style={{ fontSize:12, color:textSoft, fontWeight:600, letterSpacing:".02em" }}>
              {displayedItems.length > 0 ? `${displayedItems.length} listing${displayedItems.length!==1?"s":""} found` : "No listings match your search"}
            </div>
            <div style={{ display:"flex", gap:4, padding:3, borderRadius:10, background:midBg, border:`1px solid ${border}`, flexShrink:0 }}>
              <button onClick={()=>setViewMode("gallery")} aria-label="Gallery view" title="Gallery view"
                style={{ display:"flex", alignItems:"center", gap:5, padding:"6px 10px", borderRadius:7, border:"none", cursor:"pointer", fontFamily:"inherit", fontSize:11, fontWeight:700, transition:"all .15s", background: viewMode==="gallery" ? panelBg : "transparent", color: viewMode==="gallery" ? amber : textSoft, boxShadow: viewMode==="gallery" ? "0 1px 4px rgba(0,0,0,0.12)" : "none" }}>
                <Images size={14}/> {!isMobile && "Gallery"}
              </button>
              <button onClick={()=>setViewMode("grid")} aria-label="Grid view" title="Grid view"
                style={{ display:"flex", alignItems:"center", gap:5, padding:"6px 10px", borderRadius:7, border:"none", cursor:"pointer", fontFamily:"inherit", fontSize:11, fontWeight:700, transition:"all .15s", background: viewMode==="grid" ? panelBg : "transparent", color: viewMode==="grid" ? amber : textSoft, boxShadow: viewMode==="grid" ? "0 1px 4px rgba(0,0,0,0.12)" : "none" }}>
                <LayoutGrid size={14}/> {!isMobile && "Grid"}
              </button>
            </div>
          </div>
        )}

        <div style={{ display:"flex", gap:20, alignItems:"flex-start" }}>

          {/* GRID */}
          <div style={{ flex:1, minWidth:0 }}>
            {isLoading ? (
              <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"80px 24px", background:panelBg, borderRadius:16, border:`1px solid ${border}` }}>
                <Loader2 size={36} style={{ color:amber, animation:"spin 1s linear infinite", marginBottom:12 }}/>
                <div style={{ fontSize:14, color:textMid, fontWeight:600 }}>Loading listings…</div>
              </div>
            ) : displayedItems.length === 0 ? (
              <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"80px 24px", background:panelBg, borderRadius:16, border:`1px solid ${border}`, textAlign:"center" }}>
                <div style={{ fontSize:48, marginBottom:16 }}>🛒</div>
                <div style={{ fontSize:18, fontWeight:800, color:text, marginBottom:8 }}>No listings yet</div>
                <div style={{ fontSize:14, color:textMid, marginBottom:24, maxWidth:320 }}>Be the first to list pilot car equipment on PilotLink TradeLink.</div>
                <div style={{ display:"flex", gap:16, flexWrap:"wrap", justifyContent:"center" }}>
                  {[["🔦","Strobe Lights"],["📻","CB Radios"],["🚗","Pilot Vehicles"],["🚩","Oversize Signs"]].map(([ic,lbl])=>(
                    <div key={lbl} style={{ padding:"10px 16px", borderRadius:10, background:midBg, border:`1px solid ${border}`, fontSize:13, color:textMid, fontWeight:600 }}>{ic} {lbl}</div>
                  ))}
                </div>
              </div>
            ) : (
              <div style={{ display:"grid", gridTemplateColumns: viewMode==="grid" ? (isMobile ? "repeat(3,1fr)" : `repeat(auto-fill,minmax(150px,1fr))`) : (isMobile ? "repeat(2,1fr)" : `repeat(auto-fill,minmax(${selectedItem?"220px":"240px"},1fr))`), gap: viewMode==="grid" ? "clamp(8px,1.5vw,12px)" : "clamp(10px,2vw,18px)" }}>
                {displayedItems.map(item => {
                  const sellerName = formatName(item.seller);
                  const isSelected = !isMobile && selectedItem?.id === item.id;
                  const isSaved = savedItems.includes(item.id);
                  const compact = viewMode === "grid";
                  return (
                    <div key={item.id} onClick={()=>handleItemSelect(item)}
                      style={{ background:panelBg, borderRadius: compact?12:16, border:`2px solid ${isSelected ? amber : border}`, overflow:"hidden", cursor:"pointer", transition:"all .18s", boxShadow: isSelected ? `0 0 0 3px ${amber}30,0 8px 24px rgba(0,0,0,0.18)` : D?"0 2px 8px rgba(0,0,0,0.20)":"0 2px 8px rgba(13,31,60,0.07)" }}
                      onMouseEnter={e=>{ if(!isSelected){ e.currentTarget.style.transform="translateY(-3px)"; e.currentTarget.style.boxShadow=D?"0 8px 24px rgba(0,0,0,0.30)":"0 8px 24px rgba(13,31,60,0.12)"; }}}
                      onMouseLeave={e=>{ if(!isSelected){ e.currentTarget.style.transform="none"; e.currentTarget.style.boxShadow=D?"0 2px 8px rgba(0,0,0,0.20)":"0 2px 8px rgba(13,31,60,0.07)"; }}}
                    >
                      {/* Image */}
                      <div style={{ height: compact ? "clamp(90px,14vw,120px)" : "clamp(140px,18vw,180px)", background:midBg, position:"relative", overflow:"hidden" }}>
                        {item.images?.length > 0
                          ? <img src={item.images[0].image} alt={item.title} style={{ width:"100%", height:"100%", objectFit:"cover", transition:"transform .4s" }} onMouseEnter={e=>e.target.style.transform="scale(1.05)"} onMouseLeave={e=>e.target.style.transform="none"}/>
                          : <div style={{ width:"100%", height:"100%", display:"flex", alignItems:"center", justifyContent:"center", fontSize: compact?24:40 }}>📦</div>
                        }
                        {!compact && (
                          <button onClick={e=>toggleSave(e,item.id)} style={{ position:"absolute", top:10, right:10, width:32, height:32, borderRadius:"50%", background:"rgba(0,0,0,0.40)", border:"none", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", backdropFilter:"blur(4px)", color:isSaved?"#ef4444":"#fff", fontSize:15, transition:"all .15s" }}>
                            <Heart size={14} style={{ fill: isSaved?"#ef4444":"none", color: isSaved?"#ef4444":"#fff" }}/>
                          </button>
                        )}
                        {isVerifiedListing(item) && (
                          <div style={{ position:"absolute", bottom: compact?4:8, left: compact?4:8, padding: compact?"1px 5px":"3px 8px", borderRadius:6, background:"rgba(34,197,94,0.90)", color:"#fff", fontSize: compact?8:10, fontWeight:700, backdropFilter:"blur(4px)" }}>✓{!compact && " Verified"}</div>
                        )}
                        {/* Condition badge top-left */}
                        {item.condition && !compact && (
                          <div style={{ position:"absolute", top:8, left:8, padding:"2px 7px", borderRadius:5, background:"rgba(0,0,0,0.55)", color:"#fff", fontSize:9, fontWeight:700, backdropFilter:"blur(4px)", textTransform:"uppercase", letterSpacing:".05em" }}>{item.condition}</div>
                        )}
                        {/* Photo count — bottom-right, the one free corner — hints there's a gallery to browse */}
                        {item.images?.length > 1 && !compact && (
                          <div style={{ position:"absolute", bottom:8, right:8, padding:"2px 7px", borderRadius:5, background:"rgba(0,0,0,0.55)", color:"#fff", fontSize:9, fontWeight:700, backdropFilter:"blur(4px)", display:"flex", alignItems:"center", gap:3 }}>
                            <ImageIcon size={10}/> {item.images.length}
                          </div>
                        )}
                      </div>
                      {/* Card Body */}
                      <div style={{ padding: compact ? "8px 9px" : "12px 14px" }}>
                        {item.category && !compact && (
                          <div style={{ display:"inline-block", fontSize:9, fontWeight:800, color:amber, background:`${amber}18`, border:`1px solid ${amber}30`, borderRadius:5, padding:"2px 7px", textTransform:"uppercase", letterSpacing:".05em", marginBottom:6 }}>
                            {categoryLabel(item.category)}
                          </div>
                        )}
                        <h3 style={{ fontSize: compact?11:13, fontWeight:800, color:text, margin:"0 0 4px", lineHeight:1.3, display:"-webkit-box", WebkitLineClamp: compact?1:2, WebkitBoxOrient:"vertical", overflow:"hidden" }}>{item.title}</h3>
                        {/* Meta row: seller · location · time — hidden in compact grid mode to keep cards small */}
                        {!compact && (
                          <div style={{ fontSize:11, color:textSoft, marginBottom:8, display:"flex", alignItems:"center", gap:4, flexWrap:"wrap" }}>
                            <span>👤</span>
                            <span style={{ overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{sellerName}</span>
                            {item.location && <><span>·</span><span>📍</span><span style={{ overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{item.location}</span></>}
                            {item.created_at && <><span>·</span><span style={{ flexShrink:0, color:textSoft }}>{timeAgo(item.created_at)}</span></>}
                          </div>
                        )}
                        <div style={{ fontSize: compact?15:20, fontWeight:900, color:amber, marginBottom: compact?6:10, fontFamily:"'Plus Jakarta Sans',sans-serif" }}>${Number(item.price).toLocaleString()}</div>
                        {/* Actions */}
                        {compact ? (
                          <button onClick={e=>handleMsgClick(e,item)} style={{ width:"100%", padding:"6px", borderRadius:7, border:"none", background:`linear-gradient(135deg,${amber},${amberL})`, color:"#0d1f3c", fontSize:10, fontWeight:800, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:4, fontFamily:"inherit" }}>
                            <MessageSquare size={10}/> Message
                          </button>
                        ) : (
                        <div style={{ display:"flex", gap:7 }}>

                          <button onClick={e=>{e.stopPropagation();handleItemSelect(item);}} style={{ flex:1, padding:"9px 7px", minHeight:40, borderRadius:9, border:`1.5px solid ${border}`, background:midBg, color:textMid, fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit" }}>
                            Details
                          </button>
                          <button onClick={e=>handleMsgClick(e,item)} style={{ flex:1, padding:"9px 7px", minHeight:40, borderRadius:9, border:"none", background:`linear-gradient(135deg,${amber},${amberL})`, color:"#0d1f3c", fontSize:12, fontWeight:800, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:5, fontFamily:"inherit", boxShadow:`0 2px 8px ${amber}40` }}>
                            <MessageSquare size={11}/> Message
                          </button>
                        </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* DESKTOP SIDEBAR */}
          {!isMobile && selectedItem && (
            <div style={{ width:340, flexShrink:0, position:"sticky", top:80 }}>
              <div style={{ background:panelBg, borderRadius:18, border:`1px solid ${border}`, boxShadow:D?"0 8px 32px rgba(0,0,0,0.30)":"0 8px 32px rgba(13,31,60,0.12)", overflow:"hidden" }}>
                {!showDesktopChat ? (
                  <>
                    {/* Image */}
                    <div style={{ height:200, background:midBg, position:"relative" }}>
                      <button onClick={handleCloseSidebar} style={{ position:"absolute", top:10, right:10, zIndex:10, width:32, height:32, borderRadius:"50%", background:"rgba(0,0,0,0.45)", border:"none", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", backdropFilter:"blur(4px)" }}>
                        <X size={15}/>
                      </button>
                      <ImageGallery key={selectedItem.id} images={selectedItem.images} alt={selectedItem.title} height={200}/>
                      {/* Condition pill on image */}
                      {selectedItem.condition && (
                        <div style={{ position:"absolute", bottom:10, left:10, padding:"4px 10px", borderRadius:6, background:"rgba(0,0,0,0.60)", color:"#fff", fontSize:11, fontWeight:700, backdropFilter:"blur(4px)", textTransform:"uppercase", letterSpacing:".06em" }}>{selectedItem.condition}</div>
                      )}
                    </div>
                    {/* Details */}
                    <div style={{ padding:"18px 20px" }}>
                      <h2 style={{ fontSize:16, fontWeight:900, color:text, margin:"0 0 4px", lineHeight:1.3 }}>{selectedItem.title}</h2>
                      <div style={{ fontSize:24, fontWeight:900, color:amber, fontFamily:"'Plus Jakarta Sans',sans-serif", marginBottom:10 }}>${Number(selectedItem.price).toLocaleString()}</div>

                      {/* Location + time + condition row */}
                      <div style={{ display:"flex", flexWrap:"wrap", gap:8, marginBottom:14, alignItems:"center" }}>
                        {selectedItem.location && (
                          <div style={{ fontSize:12, color:textMid, display:"flex", alignItems:"center", gap:4 }}>
                            📍 {selectedItem.location}{selectedItem.zipcode ? `, ${selectedItem.zipcode}` : ""}
                          </div>
                        )}
                        {selectedItem.created_at && (
                          <div style={{ fontSize:11, color:textSoft, display:"flex", alignItems:"center", gap:3 }}>
                            🕒 {timeAgo(selectedItem.created_at)}
                          </div>
                        )}
                      </div>

                      {/* Seller card */}
                      <div style={{ display:"flex", alignItems:"center", gap:12, padding:"12px 14px", borderRadius:12, background:midBg, border:`1px solid ${border}`, marginBottom:14 }}>
                        <div style={{ width:40, height:40, borderRadius:"50%", background:`${amber}20`, border:`2px solid ${amber}30`, display:"flex", alignItems:"center", justifyContent:"center", fontWeight:800, fontSize:14, color:amber, flexShrink:0 }}>
                          {getInitials(formatName(selectedItem.seller))}
                        </div>
                        <div style={{ minWidth:0 }}>
                          <div style={{ fontSize:14, fontWeight:700, color:text, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{formatName(selectedItem.seller)}</div>
                          {isVerifiedListing(selectedItem) ? (
                            <div style={{ fontSize:11, color:"#22c55e", fontWeight:700 }}>✓ Verified Seller</div>
                          ) : (
                            <div style={{ fontSize:11, color:textSoft, fontWeight:600 }}>Unverified Seller</div>
                          )}
                        </div>
                      </div>

                      {/* Description (truncated) */}
                      {selectedItem.description && (
                        <>
                          <div style={{ fontSize:11, fontWeight:800, color:textSoft, textTransform:"uppercase", letterSpacing:".08em", marginBottom:5 }}>Details</div>
                          <p style={{ fontSize:13, color:textMid, lineHeight:1.7, marginBottom:16, display:"-webkit-box", WebkitLineClamp:4, WebkitBoxOrient:"vertical", overflow:"hidden" }}>{selectedItem.description}</p>
                        </>
                      )}

                      {/* Ask / Make Offer buttons (OfferUp-style) */}
                      <div style={{ display:"flex", gap:9 }}>
                        <button
                          onClick={e=>{ setShowDesktopChat(false); handleMsgClick(e, selectedItem); }}
                          style={{ flex:1, padding:"11px", borderRadius:10, border:`2px solid ${amber}`, background:"transparent", color:amber, fontWeight:800, fontSize:13, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:6, fontFamily:"inherit" }}
                        >
                          <MessageSquare size={14}/> Ask
                        </button>
                        <button
                          onClick={e=>handleMsgClick(e,selectedItem)}
                          style={{ flex:1.4, padding:"11px", borderRadius:10, border:"none", cursor:"pointer", background:`linear-gradient(135deg,${amber},${amberL})`, color:"#0d1f3c", fontWeight:800, fontSize:13, display:"flex", alignItems:"center", justifyContent:"center", gap:6, fontFamily:"inherit", boxShadow:`0 4px 14px ${amber}40` }}
                        >
                          Make Offer
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <InlineSidebarChat currentItem={selectedItem} onClose={()=>setShowDesktopChat(false)} />
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* MOBILE DETAILS MODAL (OfferUp-style) */}
      {isMobile && showMobileModal && selectedItem && (
        <div onClick={()=>setShowMobileModal(false)} style={{ position:"fixed", inset:0, zIndex:50, display:"flex", alignItems:"flex-end", justifyContent:"center", background:"rgba(0,0,0,0.55)", backdropFilter:"blur(4px)" }}>
          <div onClick={e=>e.stopPropagation()} style={{ background:panelBg, width:"100%", maxHeight:"92vh", borderRadius:"24px 24px 0 0", overflowY:"auto", position:"relative" }}>
            {/* Close */}
            <button onClick={()=>setShowMobileModal(false)} style={{ position:"absolute", top:14, right:14, zIndex:10, width:32, height:32, borderRadius:"50%", background:"rgba(0,0,0,0.40)", border:"none", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", backdropFilter:"blur(4px)" }}><X size={15}/></button>

            {/* Hero image */}
            <div style={{ height:260, background:midBg, position:"relative" }}>
              <ImageGallery key={selectedItem.id} images={selectedItem.images} alt={selectedItem.title} height={260}/>
              {selectedItem.condition && (
                <div style={{ position:"absolute", bottom:10, left:14, padding:"4px 10px", borderRadius:6, background:"rgba(0,0,0,0.65)", color:"#fff", fontSize:11, fontWeight:700, backdropFilter:"blur(4px)", textTransform:"uppercase", letterSpacing:".06em" }}>{selectedItem.condition}</div>
              )}
            </div>

            <div style={{ padding:"18px 18px 36px" }}>
              {/* Category badge */}
              <div style={{ fontSize:11, fontWeight:700, color:amber, textTransform:"uppercase", letterSpacing:".1em", marginBottom:6 }}>{categoryLabel(selectedItem.category)}</div>

              {/* Title & Price */}
              <h1 style={{ fontSize:22, fontWeight:900, color:text, marginBottom:6, lineHeight:1.2 }}>{selectedItem.title}</h1>
              <div style={{ fontSize:28, fontWeight:900, color:amber, fontFamily:"'Plus Jakarta Sans',sans-serif", marginBottom:12 }}>${Number(selectedItem.price).toLocaleString()}</div>

              {/* Location / time meta */}
              <div style={{ display:"flex", gap:12, marginBottom:18, flexWrap:"wrap", alignItems:"center" }}>
                {selectedItem.location && (
                  <div style={{ fontSize:13, color:textMid, display:"flex", alignItems:"center", gap:5 }}>📍 {selectedItem.location}</div>
                )}
                {selectedItem.created_at && (
                  <div style={{ fontSize:12, color:textSoft }}>🕒 {timeAgo(selectedItem.created_at)}</div>
                )}
              </div>

              {/* Seller */}
              <div style={{ display:"flex", alignItems:"center", gap:12, padding:"14px 16px", borderRadius:14, background:midBg, border:`1px solid ${border}`, marginBottom:18 }}>
                <div style={{ width:44, height:44, borderRadius:"50%", background:`${amber}20`, border:`2px solid ${amber}30`, display:"flex", alignItems:"center", justifyContent:"center", fontWeight:800, fontSize:16, color:amber, flexShrink:0 }}>{getInitials(formatName(selectedItem.seller))}</div>
                <div>
                  <div style={{ fontSize:15, fontWeight:800, color:text }}>{formatName(selectedItem.seller)}</div>
                  {isVerifiedListing(selectedItem) ? (
                    <div style={{ fontSize:12, color:"#22c55e", fontWeight:700 }}>✓ Verified Seller</div>
                  ) : (
                    <div style={{ fontSize:12, color:textSoft, fontWeight:600 }}>Unverified Seller</div>
                  )}
                </div>
              </div>

              {/* Details section */}
              {selectedItem.description && (
                <>
                  <div style={{ fontSize:13, fontWeight:800, color:text, textTransform:"uppercase", letterSpacing:".08em", marginBottom:8 }}>Details</div>
                  <p style={{ fontSize:14, color:textMid, lineHeight:1.75, marginBottom:22 }}>{selectedItem.description}</p>
                </>
              )}

              {/* OfferUp-style Ask / Make Offer buttons */}
              <div style={{ display:"flex", gap:10 }}>
                <button
                  onClick={e=>{ setShowMobileModal(false); handleMsgClick(e, selectedItem); }}
                  style={{ flex:1, padding:"14px", borderRadius:12, border:`2px solid ${amber}`, cursor:"pointer", background:"transparent", color:amber, fontWeight:800, fontSize:15, display:"flex", alignItems:"center", justifyContent:"center", gap:8, fontFamily:"inherit" }}
                >
                  <MessageSquare size={16}/> Ask
                </button>
                <button
                  onClick={e=>handleMsgClick(e,selectedItem)}
                  style={{ flex:1.5, padding:"14px", borderRadius:12, border:"none", cursor:"pointer", background:`linear-gradient(135deg,${amber},${amberL})`, color:"#0d1f3c", fontWeight:800, fontSize:15, display:"flex", alignItems:"center", justifyContent:"center", gap:8, fontFamily:"inherit", boxShadow:`0 4px 18px ${amber}40` }}
                >
                  Make Offer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showPremiumChat && selectedItem && <PremiumMessagingCenter currentItem={selectedItem} onClose={()=>setShowPremiumChat(false)}/>}

      <style>{`
        @keyframes spin{to{transform:rotate(360deg);}}
        .hide-scrollbar::-webkit-scrollbar{display:none;}
        .hide-scrollbar{-ms-overflow-style:none;scrollbar-width:none;}
        @media (hover:none){ button:active{ transform:scale(0.97); } }
      `}</style>
      <Footer isDarkMode={isDarkMode}/>
    </div>
  );
}
